import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const DEFAULT_ANIMATION_SPEED = 300;

/** Pointer travel, in pixels, before a press on the tablist counts as a drag rather than a click. */
const DRAG_THRESHOLD = 5;

/**
 * Per-tab overrides a block can set, as `panel attribute -> [custom property, unit]`. The block
 * writes these onto its panel; {@link TabsSectionComponent} copies them onto the tab button.
 *
 * Keep this list narrow. Anything here is set inline on the button and so silently outranks the
 * section's own value, leaving that setting looking broken in the editor — colors were tried here
 * and pulled back out for exactly that reason.
 * @type {Record<string, [string, string]>}
 */
const TAB_STYLE_OVERRIDES = {
  'data-tab-radius': ['--tab-radius', 'px'],
};

/**
 * @typedef {Object} TabsSectionRefs
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLElement[]} [tabLabels]
 * @property {HTMLElement[]} [tabMedia]
 * @property {HTMLElement} [tablist]
 * @property {HTMLButtonElement} [previousButton]
 * @property {HTMLButtonElement} [nextButton]
 */

/**
 * Tabbed content section: one tab button per `_tab` block, each controlling the
 * visibility of the panel that block renders.
 *
 * @extends {Component<TabsSectionRefs>}
 */
class TabsSectionComponent extends Component {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {Animation | null} */
  #fade = null;
  /** @type {{ pointerId: number, startX: number, scrollLeft: number, moved: boolean } | null} */
  #drag = null;
  #swallowClick = false;
  /** Index the strip was collapsed from, or -1 when a panel is open. */
  #collapsedIndex = -1;
  /** @type {IntersectionObserver | null} */
  #warmObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };

    this.addEventListener('keydown', this.#handleKeydown, opts);
    this.addEventListener('pointerdown', this.#handlePointerDown, opts);
    // Images and links inside a tab are natively draggable, which would start a ghost drag
    // instead of scrolling the strip.
    this.addEventListener('dragstart', this.#preventNativeDrag, opts);
    this.addEventListener('click', this.#swallowClickAfterDrag, { ...opts, capture: true });

    // Reveal a hidden panel when its block is selected in the theme editor.
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, opts);

    this.#syncLabels();
    this.#syncMedia();
    this.#syncTabStyles();
    this.#syncNav();
    this.#warmHiddenPanels();
  }

  updatedCallback() {
    super.updatedCallback();

    if ((this.refs.panels ?? []).some((panel) => !panel.hasAttribute('hidden'))) {
      this.#collapsedIndex = -1;
      this.classList.remove('tabs-section--collapsed');
    }

    this.#syncLabels();
    this.#syncMedia();
    this.#syncTabStyles();
    this.#syncNav();
    this.#warmHiddenPanels();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
    this.#warmObserver?.disconnect();
    this.#warmObserver = null;
    this.#fade?.cancel();
    this.#fade = null;
    window.removeEventListener('pointermove', this.#handleDragMove);
    window.removeEventListener('pointerup', this.#endDrag);
    window.removeEventListener('pointercancel', this.#endDrag);
    this.#drag = null;
  }

  /**
   * Downloads the images in the closed tabs ahead of time, so switching tabs shows them straight
   * away instead of a blurred preview (Theme settings → Media loading).
   *
   * A closed tab is `display: none`, and the browser won't fetch a lazy image there until it opens.
   * Switching those images to eager fetches them in the background, even while hidden. It waits
   * until the page has finished loading and the section is within a screen of the viewport, so the
   * downloads never compete with what the visitor is looking at. Videos stay click-to-play.
   */
  #warmHiddenPanels() {
    this.#warmObserver?.disconnect();

    const warm = () => {
      for (const panel of this.refs.panels ?? []) {
        if (!panel.hasAttribute('hidden')) continue;

        for (const img of panel.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';
      }
    };

    this.#warmObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#warmObserver?.disconnect();
        this.#warmObserver = null;

        if (document.readyState === 'complete') {
          warm();
        } else {
          window.addEventListener('load', warm, { once: true, signal: this.#abort?.signal });
        }
      },
      { rootMargin: '100% 0px' }
    );
    this.#warmObserver.observe(this);
  }

  /** @returns {boolean} Whether panel changes should cross-fade. */
  get #animates() {
    return this.hasAttribute('data-animate') && !prefersReducedMotion();
  }

  /** @returns {number} The full fade duration in milliseconds. */
  get #duration() {
    return Number(this.dataset.animationSpeed) || DEFAULT_ANIMATION_SPEED;
  }

  /**
   * Activates a tab. Bound declaratively via `on:click="/select/{index}"`.
   * @param {number} index - The index of the tab to activate.
   */
  select = (index) => {
    // The theme binds `on:click` on document, which captures ahead of this component's own
    // handler, so the drag guard has to live here to beat it.
    if (this.#swallowClick) {
      this.#swallowClick = false;
      return;
    }

    if (this.#collapsible(index) && this.refs.tabs?.[index]?.getAttribute('aria-selected') === 'true') {
      this.#collapse();
      return;
    }

    this.#activate(index);
  };

  /**
   * Whether a tab closes when it is clicked while open. Only the image tab offers this: it stays
   * legible as a standalone row once its panel is gone, which a plain text tab strip does not.
   * The block marks itself on its panel, the same route its label and radius take.
   * @param {number} index
   */
  #collapsible(index) {
    return this.refs.panels?.[index]?.hasAttribute('data-tab-collapsible') ?? false;
  }

  /**
   * Closes the open panel, leaving the strip on its own and the section only as tall as the tabs.
   * No tab reads as selected while closed, but the one that was keeps its place in the tab order
   * so the keyboard still has a way back in.
   */
  #collapse() {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];
    const current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    if (current === -1) return;

    this.#fade?.cancel();
    this.#fade = null;

    for (const [i, tab] of tabs.entries()) {
      tab.setAttribute('aria-selected', 'false');
      if (this.#collapsible(i)) tab.setAttribute('aria-expanded', 'false');
    }

    this.#collapsedIndex = current;
    this.classList.add('tabs-section--collapsed');
    this.#syncNav();

    const outgoing = panels[current];
    this.#pauseMedia(outgoing);

    if (!outgoing || !this.#animates) {
      this.#show(-1);
      return;
    }

    this.#fade = outgoing.animate({ opacity: [1, 0] }, { duration: this.#duration / 2, easing: 'ease-in', fill: 'forwards' });
    this.#fade.finished
      .then(() => {
        this.#show(-1);
        this.#fade?.cancel();
        this.#fade = null;
      })
      .catch(() => {});
  }

  /**
   * The tab the arrows step from: the selected one, or the one the strip was closed from, so
   * closing a panel does not strand the arrows with nowhere to go.
   * @returns {number}
   */
  get #currentIndex() {
    const selected = (this.refs.tabs ?? []).findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    return selected === -1 ? this.#collapsedIndex : selected;
  }

  /** Activates the tab before the current one. Bound via `on:click="/previous"`. */
  previous = () => {
    this.#step(-1);
  };

  /** Activates the tab after the current one. Bound via `on:click="/next"`. */
  next = () => {
    this.#step(1);
  };

  /**
   * Moves the selection by one tab and brings it into view. Stops at both ends rather than
   * wrapping, so the arrows' disabled state matches what a click will do.
   * @param {number} direction - `-1` for the previous tab, `1` for the next.
   */
  #step(direction) {
    const tabs = this.refs.tabs ?? [];
    const current = this.#currentIndex;
    const index = Math.min(Math.max((current === -1 ? 0 : current) + direction, 0), tabs.length - 1);
    // While closed the step lands on the remembered tab itself, which reopens it.
    if (index === current && this.#collapsedIndex === -1) return;

    this.#activate(index);
    this.#scrollIntoView(tabs[index]);
  }

  /**
   * Scrolls the tablist so a tab is fully visible, centering it when it is not. Scrolls the
   * tablist itself rather than using `scrollIntoView`, which would also move the page vertically.
   * @param {HTMLElement | undefined} tab
   */
  #scrollIntoView(tab) {
    const tablist = this.refs.tablist;
    if (!tab || !tablist) return;

    const list = tablist.getBoundingClientRect();
    const target = tab.getBoundingClientRect();
    if (target.left >= list.left && target.right <= list.right) return;

    tablist.scrollBy({
      left: target.left - list.left - (list.width - target.width) / 2,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  /** Disables whichever arrow has nowhere left to go. */
  #syncNav() {
    const { previousButton, nextButton } = this.refs;
    if (!previousButton && !nextButton) return;

    const current = this.#currentIndex;
    const tabs = this.refs.tabs ?? [];

    if (previousButton) previousButton.disabled = current <= 0;
    if (nextButton) nextButton.disabled = current === -1 || current >= tabs.length - 1;
  }

  /**
   * Applies a tab's own styling overrides, which — like the label — ride on its panel because the
   * button belongs to the section rather than to the block. Setting them inline on the button beats
   * the section's values, which are declared on the component. A tab that inherits carries no
   * attribute and is left alone, so the section's own value keeps applying.
   */
  #syncTabStyles() {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];

    for (const [i, tab] of tabs.entries()) {
      for (const [attribute, [property, unit]] of Object.entries(TAB_STYLE_OVERRIDES)) {
        const value = panels[i]?.getAttribute(attribute);

        if (value == null) tab.style.removeProperty(property);
        else tab.style.setProperty(property, `${value}${unit}`);
      }
    }
  }

  /**
   * Moves the image block a `_tab-image` block renders into its tab button. Like the label, the
   * image has to travel on the panel because a theme block's settings are not readable from the
   * section's Liquid. It is moved rather than copied so the block's `shopify_attributes` stay on a
   * live node and the theme editor can still select the image where it ends up.
   */
  #syncMedia() {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];
    const slots = this.refs.tabMedia ?? [];

    for (const [i, slot] of slots.entries()) {
      const incoming = panels[i]?.querySelector('[data-tab-media]')?.firstElementChild;

      if (!incoming) {
        // Nothing waiting: either this tab has no image, or it was lifted on an earlier pass.
        if (!slot.firstElementChild) tabs[i]?.classList.remove('tabs-section__tab--with-media');
        continue;
      }

      slot.replaceChildren(this.#unlink(incoming));
      tabs[i]?.classList.add('tabs-section__tab--with-media');
    }
  }

  /**
   * Returns the node as-is, unless it is a link — the image block renders one when it has a Link
   * set, and an anchor nested in the tab button would be invalid markup and would swallow the
   * click that selects the tab. Rebuilds it as a span, keeping the classes and styles that size it.
   * @param {Element} node
   * @returns {Element}
   */
  #unlink(node) {
    // Moving the node empties its holder; the rebuilt span does not, so take the anchor out by
    // hand. Left behind, it would be picked up as the pending image on the next pass and lifted
    // in — now empty — over the one already in place.
    if (!(node instanceof HTMLAnchorElement)) return node;

    const span = document.createElement('span');
    for (const { name, value } of node.attributes) {
      if (name === 'href' || name === 'target' || name === 'rel') continue;
      span.setAttribute(name, value);
    }
    span.append(...node.childNodes);
    node.remove();
    return span;
  }

  /**
   * Starts a drag when the press lands on a tablist that has somewhere to scroll. Touch is left
   * alone: the strip already scrolls natively there, with momentum and rubber-banding that
   * driving `scrollLeft` by hand would throw away.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    // A press always precedes a click, so this is the point at which a suppression left over
    // from an earlier drag has expired.
    this.#swallowClick = false;

    const tablist = this.refs.tablist;
    const target = event.target;

    if (event.pointerType === 'touch' || event.button !== 0) return;
    if (!tablist || !(target instanceof Node) || !tablist.contains(target)) return;
    if (tablist.scrollWidth <= tablist.clientWidth) return;

    this.#drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: tablist.scrollLeft,
      moved: false,
    };

    // On the window, so a drag that wanders off the strip — or off the page — still tracks.
    window.addEventListener('pointermove', this.#handleDragMove);
    window.addEventListener('pointerup', this.#endDrag);
    window.addEventListener('pointercancel', this.#endDrag);
  };

  /** @param {PointerEvent} event */
  #handleDragMove = (event) => {
    const drag = this.#drag;
    const tablist = this.refs.tablist;
    if (!drag || !tablist || event.pointerId !== drag.pointerId) return;

    const distance = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(distance) < DRAG_THRESHOLD) return;

    if (!drag.moved) {
      drag.moved = true;
      tablist.classList.add('tabs-section__tablist--dragging');
    }

    // Keeps the drag from turning into a text selection across the labels.
    event.preventDefault();
    tablist.scrollLeft = drag.scrollLeft - distance;
  };

  /** @param {PointerEvent} event */
  #endDrag = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    window.removeEventListener('pointermove', this.#handleDragMove);
    window.removeEventListener('pointerup', this.#endDrag);
    window.removeEventListener('pointercancel', this.#endDrag);

    this.refs.tablist?.classList.remove('tabs-section__tablist--dragging');
    // Releasing over a tab fires a click on it; after a real drag that would select a tab the
    // reader was only scrolling past.
    this.#swallowClick = drag.moved;
    this.#drag = null;
  };

  /** @param {Event} event */
  #preventNativeDrag = (event) => {
    const target = event.target;
    if (target instanceof Node && this.refs.tablist?.contains(target)) event.preventDefault();
  };

  /** @param {MouseEvent} event */
  #swallowClickAfterDrag = (event) => {
    if (!this.#swallowClick) return;
    this.#swallowClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || target.getAttribute('role') !== 'tab') return;

    const tabs = this.refs.tabs ?? [];
    const current = tabs.indexOf(target);
    if (current === -1) return;

    let next;
    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next, { focus: true });
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Node) || !this.contains(target)) return;

    const panels = this.refs.panels ?? [];
    const index = panels.findIndex((panel) => panel === target || panel.contains(target));
    if (index !== -1) this.#activate(index);
  };

  /**
   * Copies each panel's `data-tab-label` into its tab button. Theme blocks
   * can't expose their settings to the section's Liquid, so the label travels
   * on the panel (rendered by the block) and is lifted into the tablist here.
   */
  #syncLabels() {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];
    const labels = this.refs.tabLabels ?? [];

    for (const [i, tab] of tabs.entries()) {
      const label = panels[i]?.getAttribute('data-tab-label');
      if (label == null) continue;

      // Write into the label slot, not the button: the button also holds the image and chevron.
      const target = labels[i] ?? tab;
      if (target.textContent !== label) target.textContent = label;
    }
  }

  /**
   * @param {number} index - The index of the tab to activate.
   * @param {{ focus?: boolean }} [options]
   */
  #activate(index, { focus = false } = {}) {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];
    const nextTab = tabs[index];
    if (!nextTab) return;

    // Drop any fade still running, so a quick second click can't let the first
    // one finish and swap in the panel it was headed for.
    this.#fade?.cancel();
    this.#fade = null;

    this.#collapsedIndex = -1;
    this.classList.remove('tabs-section--collapsed');

    for (const [i, tab] of tabs.entries()) {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      if (this.#collapsible(i)) tab.setAttribute('aria-expanded', selected ? 'true' : 'false');
    }

    this.#syncNav();

    if (focus) nextTab.focus();

    const outgoing = panels.find((panel) => !panel.hasAttribute('hidden'));
    if (outgoing === panels[index]) return;

    this.#pauseMedia(outgoing);

    if (!this.#animates) {
      this.#show(index);
      return;
    }

    const half = this.#duration / 2;

    // Reopening from closed: nothing to fade out, so the panel just fades in.
    if (!outgoing) {
      this.#show(index);
      panels[index]?.animate({ opacity: [0, 1] }, { duration: half, easing: 'ease-out' });
      return;
    }

    this.#fade = outgoing.animate({ opacity: [1, 0] }, { duration: half, easing: 'ease-in', fill: 'forwards' });
    this.#fade.finished
      .then(() => {
        this.#show(index);
        // The panel is hidden by now, so releasing the held opacity is invisible.
        this.#fade?.cancel();
        this.#fade = null;
        panels[index]?.animate({ opacity: [0, 1] }, { duration: half, easing: 'ease-out' });
      })
      .catch(() => {});
  }

  /**
   * Stops any video playing inside a panel. Hiding a panel only takes it out of the layout, so a
   * video left running in it carries on — audible — underneath the tab the reader moved to.
   * @param {Element | undefined} panel
   */
  #pauseMedia(panel) {
    if (!panel) return;

    for (const media of panel.querySelectorAll('deferred-media, video')) {
      // `deferred-media` also reaches an embedded YouTube/Vimeo player and keeps its own
      // play/pause hint in step; a video rendered without that wrapper is paused directly.
      if (media instanceof HTMLVideoElement) media.pause();
      else if ('pauseMedia' in media && typeof media.pauseMedia === 'function') media.pauseMedia();
    }
  }

  /**
   * Shows one panel and hides the rest.
   * @param {number} index - The index of the panel to show.
   */
  #show(index) {
    for (const [i, panel] of (this.refs.panels ?? []).entries()) {
      if (i !== index) this.#pauseMedia(panel);
      panel.toggleAttribute('hidden', i !== index);
    }
  }
}

if (!customElements.get('tabs-section-component')) {
  customElements.define('tabs-section-component', TabsSectionComponent);
}
